import { useRef, useEffect, useCallback } from 'react';
import type * as Monaco from 'monaco-editor';
import { MonacoRangeLocker } from './MonacoRangeLocker';
import type { LockedRangesConfig } from './types';

/**
 * useMonacoLockedRanges Hook 选项
 */
export interface UseMonacoLockedRangesOptions extends LockedRangesConfig {
  /** 是否在代码变化时自动更新锁定范围 */
  autoUpdate?: boolean;

  /** 代码内容（用于触发自动更新） */
  code?: string;
}

/**
 * useMonacoLockedRanges Hook 返回值
 */
export interface UseMonacoLockedRangesReturn {
  /** 编辑器挂载时调用的函数 */
  onEditorMount: (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco
  ) => void;

  /** 手动更新锁定范围 */
  updateRanges: () => void;

  /** 启用锁定 */
  enable: () => void;

  /** 禁用锁定 */
  disable: () => void;

  /** 获取 locker 实例（高级用法） */
  getLocker: () => MonacoRangeLocker | null;
}

/**
 * React Hook for Monaco Editor Range Locking
 *
 * @example
 * ```tsx
 * const { onEditorMount } = useMonacoLockedRanges({
 *   calculator: 'typescript-function',
 *   context: { language: 'typescript' },
 *   onLockViolation: () => {
 *     message.warning('此区域已锁定');
 *   },
 *   autoUpdate: true,
 *   code: sourceCode,
 * });
 *
 * return (
 *   <Editor
 *     onMount={onEditorMount}
 *     value={sourceCode}
 *   />
 * );
 * ```
 */
export function useMonacoLockedRanges(
  options: UseMonacoLockedRangesOptions
): UseMonacoLockedRangesReturn {
  const lockerRef = useRef<MonacoRangeLocker | null>(null);
  const optionsRef = useRef(options);

  // 更新 options ref
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // 代码变化时自动更新锁定范围
  useEffect(() => {
    if (options.autoUpdate && lockerRef.current && options.code !== undefined) {
      lockerRef.current.updateRanges();
    }
  }, [options.autoUpdate, options.code]);

  // calculator 变化时更新（例如切换语言时）
  useEffect(() => {
    if (lockerRef.current && options.calculator) {
      lockerRef.current.updateCalculator(options.calculator);
    }
  }, [options.calculator]);

  // 上下文变化时更新
  useEffect(() => {
    if (lockerRef.current && options.context !== undefined) {
      lockerRef.current.updateContext(options.context);
    }
  }, [options.context]);

  // 编辑器挂载回调
  const onEditorMount = useCallback(
    (editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) => {
      // 清理旧实例
      if (lockerRef.current) {
        lockerRef.current.dispose();
      }

      // 创建新实例
      lockerRef.current = new MonacoRangeLocker({
        monaco,
        editor,
        calculator: optionsRef.current.calculator,
        context: optionsRef.current.context,
        decorationOptions: optionsRef.current.decorationOptions,
        onLockViolation: optionsRef.current.onLockViolation,
        enabled: optionsRef.current.enabled,
      });
    },
    []
  );

  // 手动更新锁定范围
  const updateRanges = useCallback(() => {
    lockerRef.current?.updateRanges();
  }, []);

  // 启用锁定
  const enable = useCallback(() => {
    lockerRef.current?.enable();
  }, []);

  // 禁用锁定
  const disable = useCallback(() => {
    lockerRef.current?.disable();
  }, []);

  // 获取 locker 实例
  const getLocker = useCallback(() => {
    return lockerRef.current;
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      lockerRef.current?.dispose();
    };
  }, []);

  return {
    onEditorMount,
    updateRanges,
    enable,
    disable,
    getLocker,
  };
}
